import { useState, useRef, useEffect } from "react";
import { FileText, ExternalLink, Maximize2, Minimize2 } from "lucide-react";
import { teachingMaterials, inlineSlidesUrl } from "@/lib/teaching-materials";
export function TeachingMaterials({ body }: { body: string }) {
  const materials = teachingMaterials(body);
  const [selected, setSelected] = useState(materials[0]?.url ?? "");
  const [expanded, setExpanded] = useState(false);
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    if (expanded) panel.current?.scrollIntoView({ block: "start" });
  }, [expanded]);
  const material = materials.find((x) => x.url === selected) ?? materials[0];
  const inline = material
    ? inlineSlidesUrl(material.url, window.location.origin)
    : null;
  return (
    <section
      ref={panel}
      className={`pb-teaching-materials${expanded ? " is-expanded" : ""}`}
      aria-label="Slides and teaching materials"
    >
      <header>
        <div>
          <p className="pb-board-eyebrow">Follow along</p>
          <h2>Slides & teaching materials</h2>
        </div>
        <FileText size={24} aria-hidden="true" />
      </header>
      {materials.length ? (
        <>
          <div className="pb-material-tabs">
            {materials.map((item) => (
              <button
                key={item.url}
                type="button"
                aria-pressed={material?.url === item.url}
                onClick={() => setSelected(item.url)}
              >
                {item.kind}: {item.title}
              </button>
            ))}
          </div>
          {material ? (
            <div className="pb-material-selected">
              <h3>{material.title}</h3>
              <a href={material.url} target="_blank" rel="noopener noreferrer">
                Open original <ExternalLink size={14} aria-hidden="true" />
              </a>
            </div>
          ) : null}
          {inline ? (
            <>
              <button
                className="pb-material-expand"
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((x) => !x)}
              >
                {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}{" "}
                {expanded ? "Return to split view" : "Expand slides"}
              </button>
              <iframe
                src={`${inline}#view=FitH&navpanes=0`}
                title={`Teaching material: ${material?.title}`}
                className="pb-slides-viewer"
              />
              <p className="pb-material-help">
                If your browser cannot display the PDF, use Open original.
              </p>
            </>
          ) : (
            <p className="pb-material-help">
              This material is hosted externally. Open the original alongside
              the video.
            </p>
          )}
        </>
      ) : (
        <div className="pb-material-empty">
          <FileText size={36} aria-hidden="true" />
          <h3>No materials attached yet</h3>
          <p>
            Slides, practical handouts and references supplied by the specialist
            appear here.
          </p>
        </div>
      )}
    </section>
  );
}
